import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import DoubleTokenLogo from '../DoubleLogo'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  width: 100%;

  @media screen and (max-width: 768px) {
    gap: 16px;
  }
`

const SectionTitle = styled.h2`
  font-family: 'Inter Display', sans-serif;
  font-weight: bold;
  font-size: 24px;
  line-height: 1;
  color: white;
  margin: 0;
  white-space: nowrap;

  @media screen and (max-width: 768px) {
    font-size: 20px;
  }
`

const FilterSection = styled.div`
  display: grid;
  grid-template-columns: max-content;
  grid-template-rows: max-content;
  place-items: start;
  position: relative;
`

const FilterText = styled.p`
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.4;
  color: white;
  margin: 0;
  margin-left: 30px;
  white-space: nowrap;
`

const ToggleContainer = styled.div`
  position: absolute;
  left: 0;
  top: 1px;
  width: 18px;
  height: 18px;
`

const Toggle = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 3px;
  border: 1px solid #444;
  background: ${(props) => (props.checked ? '#007bff' : 'transparent')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: #666;
  }

  ${(props) =>
    props.checked &&
    `
    &::after {
      content: '✓';
      color: white;
      font-size: 12px;
    }
  `}
`

const TableContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-x: auto;

  @media screen and (max-width: 768px) {
    overflow-x: scroll;
    -webkit-overflow-scrolling: touch;
  }
`

const TableHeader = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 16px 0;
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.4;
  color: white;
  opacity: 0.5;
  width: 100%;
  min-width: 800px;

  @media screen and (max-width: 768px) {
    min-width: 700px;
    gap: 12px;
    font-size: 12px;
  }
`

const HeaderColumn = styled.p`
  margin: 0;
  &.name {
    width: 200px;
    flex-shrink: 0;
  }
  &.data {
    flex: 1;
    min-width: 100px;
  }
  &.align-right {
    text-align: right;
  }

  @media screen and (max-width: 768px) {
    &.name {
      width: 150px;
    }
    &.data {
      min-width: 80px;
      font-size: 12px;
    }
  }
`

const TableRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 20px 0;
  width: 100%;
  position: relative;
  border-top: 1px solid #222328;
  border-bottom: 1px solid #222328;
  margin-bottom: -1px;
  min-width: 800px;

  @media screen and (max-width: 768px) {
    min-width: 700px;
    gap: 12px;
    padding: 16px 0;
  }
`

const TokenPairContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 200px;
  flex-shrink: 0;

  @media screen and (max-width: 768px) {
    width: 150px;
    gap: 6px;
  }
`

const PairName = styled.p`
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 1.4;
  color: white;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media screen and (max-width: 768px) {
    font-size: 14px;
  }
`

const DataColumn = styled.p`
  flex: 1;
  min-width: 100px;
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 1.4;
  color: white;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.align-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
  }

  @media screen and (max-width: 768px) {
    font-size: 14px;
    min-width: 80px;
  }
`

const PercentageChange = styled.span`
  font-family: 'Inter Display', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: ${(props) => (props.isPositive ? '#45e560' : '#ff8080')};
  white-space: nowrap;

  @media screen and (max-width: 768px) {
    font-size: 14px;
  }
`

const ArrowIcon = styled.div`
  width: 5px;
  height: 3px;
  background: ${(props) => (props.isPositive ? '#45e560' : '#ff8080')};
  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  ${(props) => !props.isPositive && 'transform: rotate(180deg);'}
`

// Hardcoded security pairs to show
const hardcodedSecPairs = [
  '0xfe3d92cf0292a4e44402d1e6a10ae8b575fa61dc', // WIXS/IXAPE
  '0x622efb1fb4a2486b75813aba428639251495eccb', // WIXS/TAU
  '0xec86ceccd8046ed956060988f91c754e7a13328f', // WIXS/CKGP
  '0x05b9cd0ec1fe6bb4e61f4437a56e4aa4b442af5a', // WIXS/MSTO
]

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const PaginationText = styled.p`
  font-family: 'Inter Display', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  white-space: nowrap;
`

const NavArrows = styled.div`
  display: flex;
  gap: 4px;
`

const NavArrow = styled.div`
  cursor: pointer;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;

  &:hover:not(.disabled) {
    background: rgba(255, 255, 255, 0.08);
  }

  &.disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const TopPairsSection = ({ pairs }) => {
  const [hideUntracked, setHideUntracked] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 5

  // Filter pairs to only show the hardcoded security pairs
  const filteredPairs = useMemo(() => {
    if (!pairs) return []

    const pairArray = Object.keys(pairs)
      .map((address) => ({
        address,
        ...pairs[address],
      }))
      .filter((pair) => {
        // Only show pairs that are in the hardcoded list
        if (!hardcodedSecPairs.includes(pair.address.toLowerCase())) return false
        if (hideUntracked && !pair.trackedReserveUSD) return false
        return pair.token0 && pair.token1
      })
      .sort((a, b) => {
        const liquidityA = a.trackedReserveUSD || a.reserveUSD || 0
        const liquidityB = b.trackedReserveUSD || b.reserveUSD || 0
        return liquidityB - liquidityA
      })

    return pairArray
  }, [pairs, hideUntracked])

  const totalPages = Math.ceil(filteredPairs.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentPairs = filteredPairs.slice(startIndex, endIndex)

  const formatValue = (value) => {
    if (!value || value === 0) return '$0'
    return `$${Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  const formatPercent = (value) => {
    if (!value || value === 0) return '+0.00%'
    const num = Number(value)
    const sign = num >= 0 ? '+' : ''
    return `${sign}${num.toFixed(2)}%`
  }

  return (
    <Container>
      <SectionTitle>Top Pairs</SectionTitle>
      <FilterSection>
        <FilterText>Hide untracked pairs</FilterText>
        <ToggleContainer>
          <Toggle checked={hideUntracked} onClick={() => setHideUntracked(!hideUntracked)} />
        </ToggleContainer>
      </FilterSection>

      <TableContainer>
        <TableHeader>
          <HeaderColumn className="name">Name</HeaderColumn>
          <HeaderColumn className="data">Liquidity</HeaderColumn>
          <HeaderColumn className="data">Volume (24hrs)</HeaderColumn>
          <HeaderColumn className="data">Volume (7d)</HeaderColumn>
          <HeaderColumn className="data">Fees (24h)</HeaderColumn>
          <HeaderColumn className="data align-right">1y Fees / Liquidity</HeaderColumn>
        </TableHeader>

        {currentPairs.map((pair) => {
          const liquidity = pair.trackedReserveUSD || pair.reserveUSD || 0
          const volume24h = pair.oneDayVolumeUSD || pair.oneDayVolumeUntracked || 0
          const volume7d = pair.oneWeekVolumeUSD || pair.oneWeekVolumeUntracked || 0
          const fees24h = volume24h * 0.003

          // Mock percentage change - in real app this would come from data
          const mockPercentChange = (Math.random() - 0.5) * 10

          return (
            <TableRow key={pair.address}>
              <TokenPairContainer>
                <DoubleTokenLogo a0={pair.token0.id} a1={pair.token1.id} size={24} margin={true} />
                <PairName>
                  {pair.token0.symbol}/{pair.token1.symbol}
                </PairName>
              </TokenPairContainer>

              <DataColumn>{formatValue(liquidity)}</DataColumn>
              <DataColumn>{formatValue(volume24h)}</DataColumn>
              <DataColumn>{formatValue(volume7d)}</DataColumn>
              <DataColumn>{formatValue(fees24h)}</DataColumn>

              <DataColumn className="align-right">
                <PercentageChange isPositive={mockPercentChange >= 0}>
                  {formatPercent(mockPercentChange)}
                </PercentageChange>
                <ArrowIcon isPositive={mockPercentChange >= 0} />
              </DataColumn>
            </TableRow>
          )
        })}
      </TableContainer>

      <PaginationContainer>
        <PaginationText>
          {startIndex + 1}-{Math.min(endIndex, filteredPairs.length)} of {filteredPairs.length}
        </PaginationText>
        <NavArrows>
          <NavArrow
            className={currentPage === 1 ? 'disabled' : ''}
            onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M10 4L6 8L10 12"
                stroke="rgba(255, 255, 255, 0.6)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </NavArrow>
          <NavArrow
            className={currentPage >= totalPages ? 'disabled' : ''}
            onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 4L10 8L6 12"
                stroke="rgba(255, 255, 255, 0.6)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </NavArrow>
        </NavArrows>
      </PaginationContainer>
    </Container>
  )
}

export default TopPairsSection
